/**
 * A wrapper to handle the Modern pagination style, infinite scroll, and
 * (regardless of style) fetching pagination links via AJAX instead of a full
 * page navigation — see handleNavClick/loadPage for the discrete
 * prev/number/next case used by "Traditional" pagination.
 *
 * Usage:
 *  <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));

      // pushState (used by loadPage for discrete nav) doesn't reload content
      // on its own, so re-fetch on back/forward.
      this.popStateHandler = this.handlePopState.bind(this);
      window.addEventListener('popstate', this.popStateHandler);
    }

    disconnectedCallback() {
      this.destroy();
      window.removeEventListener('popstate', this.popStateHandler);
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;

      // "Modern" style and/or infinite scroll treat the next control as
      // cumulative (append more results). Traditional discrete pagination
      // (prev/number/next) instead swaps the page's results — see
      // handleNavClick/loadPage below.
      this.appendMode = !!(
        this.pagination &&
        (this.pagination.dataset.paginationStyle === 'modern' || this.infiniteScroll)
      );

      // Bind click handlers to pagination results
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      // Bind click handler for discrete pagination nav (prev/number/next in
      // traditional, non-append mode).
      this.navClickHandler = this.navClickHandler || this.handleNavClick.bind(this);
      this.addEventListener('click', this.navClickHandler);

      // Bind click handler to load more button click
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.pagination.dataset.isMoreResults && this.results) {
        if (this.loadMoreButton && this.appendMode) {
          this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
        }

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadMore();

                  // Continue to load results as long as the 'load more' button is visible
                  this.intersectingTimer = setInterval(this.loadMore.bind(this), 1000);
                } else if (this.intersectingTimer) {
                  clearInterval(this.intersectingTimer);
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    /**
     * Destroys the component
     */
    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      this.removeEventListener('click', this.navClickHandler);
      this.loadMoreClickHandler = null;
    }

    /**
     * Reloads the component
     */
    reload() {
      this.destroy();
      this.init();
    }

    /**
     * Handles click on the load more button
     * @param {object} evt - Event object
     */
    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Handles a click on a discrete pagination link (prev/number/next) when
     * not in append mode — fetches that page and swaps in its results,
     * instead of letting the browser do a full navigation.
     * @param {object} evt - Event object
     */
    handleNavClick(evt) {
      if (!this.pagination) return;

      const link = evt.target.closest('a');
      if (!link || !this.pagination.contains(link)) return;
      if (link.getAttribute('aria-disabled') === 'true') return;

      // The load-more/"show more" control is cumulative and already handled
      // by handleLoadMoreClick when appendMode is on.
      if (this.appendMode && link === this.loadMoreButton) return;
      if (!link.classList.contains('js-pagination-nav') && !link.classList.contains('js-pagination-load-more')) return;

      evt.preventDefault();
      this.loadPage(link.href);
    }

    /**
     * Handles browser back/forward after a discrete pagination nav — re-fetch
     * the now-current URL's content rather than leaving stale results in the
     * DOM (pushState alone doesn't reload anything).
     */
    handlePopState() {
      if (this.pagination && this.pagination.dataset.paginationStyle === 'traditional') {
        this.loadPage(window.location.href, false);
      }
    }

    /**
     * Fetches the given pagination page and replaces the current results
     * and pagination bar with it (discrete pagination, as opposed to
     * loadMore()'s cumulative append).
     * @param {string} url - URL of the page to load.
     * @param {boolean} [pushState=true] - Push the URL to browser history.
     */
    async loadPage(url, pushState = true) {
      if (this.isFetching) return;
      this.isFetching = true;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      if (this.pagination) this.pagination.classList.add('is-loading');

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const newResults = tmpl.content.querySelectorAll('.js-pagination-result');
        const resultsParent = this.results.length ? this.results[0].parentElement : null;
        if (resultsParent && newResults.length) {
          resultsParent.innerHTML = '';
          newResults.forEach((result) => resultsParent.appendChild(result));
          this.results = this.querySelectorAll('.js-pagination-result');
        }

        const newPagination = tmpl.content.querySelector('.js-pagination');
        if (newPagination && this.pagination) {
          this.pagination.innerHTML = newPagination.innerHTML;
          this.pagination.dataset.isMoreResults = newPagination.dataset.isMoreResults || '';

          if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
          this.loadMoreButton = this.querySelector('.js-pagination-load-more');
          if (this.loadMoreButton && this.appendMode) {
            this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
          }
        }

        if (pushState) {
          history.pushState({}, '', url);
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      } catch (error) {
        console.log('Unable to fetch page of results', error); // eslint-disable-line
        window.location.href = url;
      } finally {
        this.isFetching = false;
        this.controller = null;
        if (this.pagination) this.pagination.classList.remove('is-loading');
      }
    }

    /**
     * Handles a click on a item in the pagination
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result') && this.controller) {
        this.controller.abort();
      }
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async loadMore() {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.controller = new AbortController();

        try {
          // Add the loading state to the button
          if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          // Retrieve and parse the next page
          const response = await fetch(this.loadMoreButton.href, {
            signal: this.controller.signal
          });

          if (!response.ok) throw new Error(response.status);
          if (this.dataset.pauseInfiniteScroll === 'true') {
            throw new Error('Infinite scroll was interrupted.');
          }

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Append the additional results
          let newResultsHtml = '';
          const lastResult = this.results[this.results.length - 1];
          tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
            newResultsHtml += result.outerHTML;
          });
          lastResult.insertAdjacentHTML('afterend', newResultsHtml);
          this.results = this.querySelectorAll('.js-pagination-result');

          // Update the pagination content
          const newPagination = tmpl.content.querySelector('.js-pagination');
          if (newPagination.dataset.isMoreResults) {
            this.pagination.innerHTML = newPagination.innerHTML;

            // Rebind to the new load more button
            if (this.loadMoreButton) {
              this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
              this.loadMoreButton = this.querySelector('.js-pagination-load-more');
              this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
            }
          } else {
            this.pagination.remove();
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            if (this.paginationObserver) this.paginationObserver.disconnect();
            document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
          }
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        } finally {
          this.controller = null;

          // Remove the loading state from the load more button
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
